import updateCart from '~/firebase/update/updateCart';
import { useReducer, useEffect, useCallback } from 'react';
import * as fp from 'lodash/fp';
import useCartId from '~/hooks/useCartId';
import checkTaxNumber from '~/utils/checkTaxNumber';
import { PLATFORM_ID } from '~/globalVariables';
import { TaxInfo } from '~/types/cart';

type TaxInfoState = {
  taxInfo: TaxInfo;
  errors: TaxInfo;
};

enum TaxInfoAction {
  SET_TITLE = 'SET_TITLE',
  SET_INVOICE = 'SET_INVOICE',
  SET_TAX_INFO = 'SET_TAX_INFO',
  SET_ERROR = 'SET_ERROR',
  CLEAR_ERRORS = 'CLEAR_ERRORS',
  CLEAR_INFO = 'CLEAR_INFO',
  RESET = 'RESET',
}

type TaxInfoActionState =
  | {
      type: TaxInfoAction.SET_TITLE;
      payload: string;
    }
  | {
      type: TaxInfoAction.SET_INVOICE;
      payload: string;
    }
  | {
      type: TaxInfoAction.SET_TAX_INFO;
      payload: TaxInfo;
    }
  | {
      type: TaxInfoAction.SET_ERROR;
      payload: Partial<TaxInfo>;
    }
  | {
      type: TaxInfoAction.CLEAR_ERRORS;
    }
  | {
      type: TaxInfoAction.CLEAR_INFO;
    }
  | {
      type: TaxInfoAction.RESET;
    };

function taxInfoStateReducer(state: TaxInfoState, action: TaxInfoActionState): TaxInfoState {
  switch (action.type) {
    case TaxInfoAction.SET_TITLE: {
      return {
        ...state,
        taxInfo: {
          ...state.taxInfo,
          title: action.payload,
        },
      };
    }
    case TaxInfoAction.SET_INVOICE: {
      return {
        ...state,
        taxInfo: {
          ...state.taxInfo,
          invoice: action.payload,
        },
      };
    }
    case TaxInfoAction.SET_TAX_INFO: {
      return {
        ...state,
        taxInfo: {
          ...state,
          ...action.payload,
        },
      };
    }
    case TaxInfoAction.SET_ERROR: {
      return {
        ...state,
        errors: {
          ...state.errors,
          ...action.payload,
        },
      };
    }
    case TaxInfoAction.CLEAR_ERRORS: {
      return {
        ...state,
        errors: {
          invoice: '',
          title: '',
        },
      };
    }
    case TaxInfoAction.CLEAR_INFO: {
      return {
        ...state,
        taxInfo: {
          title: '',
          invoice: '',
        },
      };
    }
    case TaxInfoAction.RESET: {
      return {
        taxInfo: {
          title: '',
          invoice: '',
        },
        errors: {
          title: '',
          invoice: '',
        },
      };
    }
    default: {
      throw new Error('no match type');
    }
  }
}

export default function useTaxInfo(
  defaultTaxInfo: TaxInfo = {
    title: '',
    invoice: '',
  },
) {
  const { cartId } = useCartId();
  const [taxInfoState, dispatch] = useReducer(taxInfoStateReducer, {
    taxInfo: defaultTaxInfo,
    errors: {
      title: '',
      invoice: '',
    },
  });

  useEffect(() => {
    dispatch({
      type: TaxInfoAction.SET_TAX_INFO,
      payload: {
        ...defaultTaxInfo,
      },
    });
  }, [defaultTaxInfo]);

  const handleUpdateTaxInfo = useCallback(
    fp.debounce(700, async (title: string, invoice: string, cartId: string) => {
      try {
        await updateCart(PLATFORM_ID, cartId, {
          taxInfo: {
            title: title,
            invoice: invoice,
          },
        });
      } catch (err) {
        console.error('update tax info error', cartId);
      }
    }),
    [],
  );

  const handleChangeTitle = (value: string) => {
    dispatch({ type: TaxInfoAction.SET_TITLE, payload: value });

    dispatch({
      type: TaxInfoAction.SET_ERROR,
      payload: { title: fp.lte(value.length, 1) ? '不能少於兩個字' : '' },
    });
    if (!cartId) {
      return;
    }

    handleUpdateTaxInfo(value, taxInfoState.taxInfo.invoice, cartId);
  };

  const handleChangeInvoice = (value: string) => {
    dispatch({ type: TaxInfoAction.SET_INVOICE, payload: value });

    const checkInvoiceError = fp.cond<string, string>([
      [(value) => !fp.eq(value.length, 8), fp.always('長度必須為8個字')],
      [
        (value) => {
          const regex = /^[0-9]{8}/;
          return !regex.test(value);
        },
        fp.always('統編只能是數字'),
      ],
      [(value) => !checkTaxNumber(value), fp.always('輸入的統編不合法')],
      [fp.T, fp.always('')],
    ]);

    dispatch({
      type: TaxInfoAction.SET_ERROR,
      payload: {
        invoice: checkInvoiceError(value.trim()),
      },
    });

    if (!cartId) {
      return;
    }

    handleUpdateTaxInfo(taxInfoState.taxInfo.title, value, cartId);
  };

  const handleChangeTaxInfo = (value: TaxInfo) => {
    dispatch({ type: TaxInfoAction.SET_TAX_INFO, payload: value });

    if (!cartId) {
      return;
    }

    handleUpdateTaxInfo(value.title, value.invoice, cartId);
  };

  const handleClearError = () => {
    dispatch({ type: TaxInfoAction.CLEAR_ERRORS });
  };

  return {
    taxInfoState,
    handleChangeTaxInfo,
    handleChangeInvoice,
    handleChangeTitle,
    handleClearError,
  };
}
